import type { HubMeeting, HubMeetingStatus, HubMeetingSystemId } from "@/meetings/model";

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function statusFrom(start: string | null, end: string | null, raw: unknown): HubMeetingStatus {
  if (raw === "live" || raw === "upcoming" || raw === "past") return raw;
  const now = Date.now();
  const a = start ? Date.parse(start) : NaN;
  const b = end ? Date.parse(end) : NaN;
  if (!Number.isFinite(a)) return "upcoming";
  if (Number.isFinite(b) && now >= a && now <= b) return "live";
  if (now < a) return "upcoming";
  return "past";
}

function systemFrom(row: Record<string, unknown>, id: string): HubMeetingSystemId {
  if (row.system === "google" || row.system === "zoom" || row.system === "hub") return row.system;
  if (id.startsWith("zoom_")) return "zoom";
  if (id.startsWith("gcal_") || id.startsWith("gmeet_")) return "google";
  return "hub";
}

export function toHubMeeting(raw: unknown, index: number): HubMeeting | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = pickString(row.id) || `meeting-${index}`;
  const start = pickString(row.start) || null;
  const end = pickString(row.end) || null;
  const joinUrl = pickString(row.joinUrl, row.join_url, row.hangoutLink);
  const attendees = Array.isArray(row.attendees)
    ? row.attendees.map((item) => (typeof item === "string" ? item.trim() : "")).filter(Boolean)
    : undefined;
  const meeting: HubMeeting = {
    id,
    title: pickString(row.title, row.topic) || "Meeting",
    start,
    end,
    status: statusFrom(start, end, row.status),
    system: systemFrom(row, id),
  };
  if (/^https:\/\//i.test(joinUrl)) meeting.joinUrl = joinUrl;
  const email = pickString(row.accountEmail, row.host);
  if (email) meeting.accountEmail = email;
  if (attendees?.length) meeting.attendees = attendees;
  const location = pickString(row.location);
  if (location && location !== meeting.joinUrl) meeting.location = location;
  return meeting;
}
